import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import type { Customer } from '@store/slices/posCustomersSlice.tsx';

import { fetchPosCustomer } from '../api/customers';
import { useCustomerBalance } from '../api/customerBalance';
import type { PosCustomerBalanceSummary } from '../types';

interface UsePosCustomerOptions {
  initialCustomerId?: number | null;
  persistCustomerId?: (customerId: number | null) => void;
  onCustomerChange?: (customer: Customer | null) => void;
}

export interface UsePosCustomerResult {
  customerId: number | null;
  customer: Customer | null;
  isHydrating: boolean;
  selectCustomer: (customer: Customer) => void;
  selectCustomerById: (customerId: number) => void;
  clearCustomer: () => void;
  balance: PosCustomerBalanceSummary | null;
  balanceIsLoading: boolean;
  refetchBalance: () => Promise<PosCustomerBalanceSummary | null>;
}

export const usePosCustomer = ({
  initialCustomerId = null,
  persistCustomerId,
  onCustomerChange,
}: UsePosCustomerOptions = {}): UsePosCustomerResult => {
  const queryClient = useQueryClient();
  const [customerId, setCustomerId] = useState<number | null>(
    initialCustomerId ?? null,
  );
  const [snapshot, setSnapshot] = useState<Customer | null>(null);

  const customerQuery = useQuery({
    queryKey: ['pos', 'customer', customerId],
    queryFn: () => fetchPosCustomer(customerId as number),
    enabled: customerId != null,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (customerQuery.data) {
      setSnapshot(customerQuery.data);
    }
  }, [customerQuery.data]);

  const activeCustomer = useMemo(() => {
    if (snapshot && snapshot.id === customerId) {
      return snapshot;
    }
    return customerQuery.data ?? null;
  }, [customerId, customerQuery.data, snapshot]);

  useEffect(() => {
    onCustomerChange?.(activeCustomer ?? null);
  }, [activeCustomer, onCustomerChange]);

  const persist = useCallback(
    (nextId: number | null) => {
      persistCustomerId?.(nextId);
    },
    [persistCustomerId],
  );

  const selectCustomer = useCallback(
    (customer: Customer) => {
      setSnapshot(customer);
      setCustomerId(customer.id);
      persist(customer.id);
    },
    [persist],
  );

  const selectCustomerById = useCallback(
    (id: number) => {
      setSnapshot(null);
      setCustomerId(id);
      persist(id);
    },
    [persist],
  );

  const clearCustomer = useCallback(() => {
    setSnapshot(null);
    setCustomerId(null);
  }, []);

  const balanceQuery = useCustomerBalance(customerId, {
    enabled: customerId != null,
  });

  useEffect(() => {
    if (customerId == null) {
      queryClient.removeQueries({ queryKey: ['pos', 'customer'], exact: false });
      queryClient.removeQueries({ queryKey: ['pos', 'customer-balance'], exact: false });
    }
  }, [customerId, queryClient]);

  return {
    customerId,
    customer: activeCustomer,
    isHydrating: customerQuery.isLoading || customerQuery.isFetching,
    selectCustomer,
    selectCustomerById,
    clearCustomer,
    balance: (balanceQuery.data as PosCustomerBalanceSummary | undefined) ?? null,
    balanceIsLoading: balanceQuery.isLoading || balanceQuery.isFetching,
    refetchBalance: async () => {
      const result = await balanceQuery.refetch();
      return (result.data as PosCustomerBalanceSummary | undefined) ?? null;
    },
  };
};

export default usePosCustomer;
